import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { ButtonModule } from '@openng/optimus-ui/button';
import {
  Table,
  TableFilterEvent,
  TableModule,
  TablePageEvent,
} from '@openng/optimus-ui/table';
import { ActionMenuComponent } from '../menu/action-menu.component';
import { DEFAULT_CURRENCY_FORMAT } from '../shared/currency';
import { RowActionTriggerComponent } from './row-action-trigger.component';
import {
  ColumnType,
  defaultTableOption,
  isTemplateColumnOption,
  isTextColumnOption,
  TableColumnOption,
  TableOption,
  TableSortEvent,
} from './table';

const GENERATED_FIELD = Symbol('generatedField');

export type PrimeTableRowToggleEvent<T> = {
  row: T;
  index: number;
  expanded: boolean;
  event: MouseEvent;
};

@Component({
  selector: 'core-prime-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    ActionMenuComponent,
    RowActionTriggerComponent,
  ],
  template: `
    @let option = tableOptionWithDefaults();
    @let sort = defaultSort();
    <core-action-menu #rowActionsMenu />
    <p-table
      #table
      [dataKey]="option.dataKey"
      [sortField]="$safeNavigationMigration(sort?.field)"
      [sortOrder]="sort?.direction ?? 0"
      (sortFunction)="customSort($event)"
      (onFilter)="onTableFilter($event)"
      [columns]="columnsWithDefaults()"
      [value]="data()"
      [paginator]="option.showPaginator"
      [rowsPerPageOptions]="[5, 10, 20]"
      [rows]="option.pageSize"
      [totalRecords]="totalRecords()"
      [class]="option.tableClass"
      [rowHover]="option.selectRow"
      showLoader="false"
      scrollable="true"
      customSort="true"
      [reorderableColumns]="option.reorderable"
      [selectionMode]="option.selectRow ? 'single' : null"
      [selection]="selection()"
      [(first)]="first"
      [globalFilterFields]="globalFilterFieldsWithDefaults()"
      (onPage)="onTablePage($event)"
    >
      <ng-template pTemplate="header" let-columns>
        <tr>
          @if (option.reorderable) {
            <th class="w-12"></th>
          }
          @if (canExpand() && columns.length) {
            <th></th>
          }
          @for (column of columns; track $index) {
            @if (isHeaderTemplate(column.header)) {
              @if (canColumnSort(column)) {
                <th
                  [ngClass]="column.headerClass"
                  [pSortableColumn]="column.field"
                  pFrozenColumn
                  [frozen]="column.frozen"
                >
                  <ng-container
                    [ngTemplateOutlet]="column.header"
                    [ngTemplateOutletContext]="{ option: column }"
                  >
                  </ng-container>
                  <p-sortIcon [field]="column.field"></p-sortIcon>
                </th>
              } @else {
                <th
                  [ngClass]="column.headerClass"
                  pFrozenColumn
                  [frozen]="column.frozen"
                >
                  <ng-container
                    [ngTemplateOutlet]="column.header"
                    [ngTemplateOutletContext]="{ option: column }"
                  >
                  </ng-container>
                </th>
              }
            } @else if (canColumnSort(column)) {
              <th
                [ngClass]="column.headerClass"
                [pSortableColumn]="column.field"
                pFrozenColumn
                [frozen]="column.frozen"
              >
                {{ column.header }}
                <p-sortIcon [field]="column.field"></p-sortIcon>
              </th>
            } @else {
              <th
                [ngClass]="column.headerClass"
                pFrozenColumn
                [frozen]="column.frozen"
              >
                {{ column.header }}
              </th>
            }
          }
          @if (option.rowActions?.length) {
            <th class="w-auto whitespace-nowrap"></th>
          }
        </tr>
      </ng-template>

      <ng-template
        pTemplate="body"
        let-rowData
        let-columns="columns"
        let-rowIndex="rowIndex"
      >
        <tr
          data-testid="table-row"
          [ngClass]="
            option.selectRow ? 'hover:bg-hover hover:cursor-pointer' : ''
          "
          [pSelectableRow]="option.selectRow ? rowData : null"
          [pReorderableRow]="option.reorderable ? rowIndex : undefined"
          (click)="onRowClick(rowData, $event)"
        >
          @if (option.reorderable) {
            <td>
              <span class="pi pi-bars" pReorderableRowHandle></span>
            </td>
          }
          @if (canExpand()) {
            <td>
              <ng-container
                [ngTemplateOutlet]="toggleExpandColumn"
                [ngTemplateOutletContext]="{
                  row: rowData,
                }"
              ></ng-container>
            </td>
          }
          @for (column of columns; track $index) {
            <td
              data-testid="table-data"
              [ngClass]="column.dataClass"
              pFrozenColumn
              [frozen]="column.frozen"
              (click)="onCellClick(rowData, column, $event)"
            >
              @switch (column.type) {
                @case ('template') {
                  <ng-container
                    [ngTemplateOutlet]="setColumnTemplate(column)"
                    [ngTemplateOutletContext]="{
                      option: column,
                      row: rowData,
                      index: rowIndex,
                    }"
                  >
                  </ng-container>
                }
                @case ('toggle') {
                  <ng-container
                    [ngTemplateOutlet]="toggleColumn"
                    [ngTemplateOutletContext]="{
                      option: column,
                      row: rowData,
                    }"
                  ></ng-container>
                }
                @case ('text') {
                  <ng-container
                    [ngTemplateOutlet]="textColumn"
                    [ngTemplateOutletContext]="{
                      option: column,
                      row: rowData,
                    }"
                  ></ng-container>
                }
              }
            </td>
          }
          @if (option.rowActions?.length) {
            <td class="w-auto whitespace-nowrap p-0">
              <core-row-action-trigger
                [row]="rowData"
                [actions]="option.rowActions!"
                [menu]="rowActionsMenu"
              />
            </td>
          }
        </tr>
      </ng-template>

      @if (option.expandTemplate; as expandTemplate) {
        <ng-template #expandedrow let-rowData>
          <tr class="bg-expand-row">
            <td
              class="py-0 px-1"
              [attr.colspan]="
                columnsWithDefaults().length +
                (option.rowActions?.length ? 1 : 0) +
                1
              "
            >
              <ng-container
                [ngTemplateOutlet]="expandTemplate"
                [ngTemplateOutletContext]="{ option, row: rowData }"
              ></ng-container>
            </td>
          </tr>
        </ng-template>
      }

      @if (option.emptyMessage; as emptyMessage) {
        <ng-template #emptymessage>
          <tr>
            <td
              [attr.colspan]="
                columnsWithDefaults().length +
                (option.rowActions?.length ? 1 : 0) +
                1
              "
            >
              {{ emptyMessage }}
            </td>
          </tr>
        </ng-template>
      }

      <ng-template #toggleColumn let-option="option" let-row="row">
        @for (column of option.columns; track $index) {
          <span class="material-icons">{{
            row[column.field] ? column.icon : (column.iconDisabled ?? '')
          }}</span>
        }
      </ng-template>

      <ng-template #textColumn let-option="option" let-row="row">
        @if (option.getFieldValue) {
          <span>{{ option.getFieldValue(row) }}</span>
        } @else if (option.dateFormat) {
          <span>{{
            row[option.field] | date: option.dateFormat : option.timezone
          }}</span>
        } @else if (option.currency) {
          <span>{{
            row[option.field]
              | currency: option.currency : 'symbol' : currencyFormat
          }}</span>
        } @else if (option.decimalPlaces) {
          <span>{{
            row[option.field] | number: '1.0-' + option.decimalPlaces
          }}</span>
        } @else {
          <span>{{ row[option.field] }}</span>
        }
      </ng-template>
    </p-table>
    <ng-template #toggleExpandColumn let-option="option" let-row="row">
      <p-button
        [plain]="true"
        [rounded]="true"
        [text]="true"
        (click)="onToggleRow(row, $event)"
        data-testid="btnToggleRow"
      >
        <span class="material-icons">{{
          table.isRowExpanded(row)
            ? 'keyboard_arrow_down'
            : 'keyboard_arrow_right'
        }}</span>
      </p-button>
    </ng-template>
  `,
  styles: [
    `
      ::ng-deep svg.p-icon {
        width: 0.875rem !important;
        height: 0.875rem !important;
      }
    `,
  ],
})
export class PrimeTableComponent<T> {
  protected readonly table = viewChild.required<Table<T>>('table');
  public readonly data = input<T[]>([]);
  public readonly totalRecords = model(0);
  public readonly displayCount = signal(0);
  public readonly columns = input<TableColumnOption<T>[]>([]);
  public readonly selection = model<T>();
  protected readonly currencyFormat = DEFAULT_CURRENCY_FORMAT;
  protected readonly columnsWithDefaults = computed(() => {
    const columns = this.columns() ?? [];
    columns.forEach((column, index) => {
      column.type ??= ColumnType.Text;
      if (isTemplateColumnOption(column) && !column.field) {
        column.field = index.toString();
        Reflect.set(column, GENERATED_FIELD, true);
      }
    });
    untracked(() => {
      const sort = this.defaultSort();
      if (sort && !this.tableOptionWithDefaults().serverSort) {
        this.customSort({
          data: this.data(),
          field: sort.field,
          order: sort.direction,
        });
      }
    });
    return columns;
  });
  public readonly globalFilterFields = model<string[]>([]);
  protected readonly globalFilterFieldsWithDefaults = computed(() => {
    const fields = this.globalFilterFields();
    if (fields.length) {
      return fields;
    }
    return this.columnsWithDefaults()
      .filter(col => col.filterable && col.field)
      .map(col => col.field as string);
  });
  protected readonly filterable = computed(
    () => this.globalFilterFieldsWithDefaults().length > 0,
  );
  public readonly filterGlobal = model('');
  public readonly defaultSort = input<{ field: string; direction: number }>();
  public readonly tableOption = input<TableOption<T>>(defaultTableOption);
  protected readonly tableOptionWithDefaults = computed(() => ({
    ...defaultTableOption,
    ...this.tableOption(),
  }));
  protected readonly canExpand = computed(() => {
    const option = this.tableOptionWithDefaults();
    return !!(option.dataKey && option.expandTemplate);
  });
  protected readonly first = model(0);

  public readonly rowClick = output<{
    row: T;
    index: number;
    event: MouseEvent;
  }>();
  public readonly pageSizeChange = output<number>();
  public readonly cellClick = output<{
    row: T;
    index: number;
    column: TableColumnOption<T>;
    event: MouseEvent;
  }>();
  public readonly rowToggle = output<PrimeTableRowToggleEvent<T>>();
  public readonly sortChange = output<TableSortEvent>();
  private lastServerSort?: TableSortEvent;

  public constructor() {
    effect(() => {
      const columns = this.columnsWithDefaults();
      const data = this.data();
      for (const column of columns) {
        const isGeneratedField = Reflect.get(column, GENERATED_FIELD) === true;
        if (!isGeneratedField || !column.getFieldValue || !column.field) {
          continue;
        }
        const field = column.field as keyof T;
        for (const row of data) {
          row[field] = column.getFieldValue(row) as T[keyof T];
        }
      }
    });

    effect(() => {
      const selection = this.selection();
      const option = this.tableOptionWithDefaults();
      const data = this.data();
      this.displayCount.set(data?.length ?? 0);
      if (
        !option.showPaginator ||
        !option.pageSize ||
        !selection ||
        !data?.length
      ) {
        return;
      }
      const index = data.indexOf(selection);
      if (index === -1) {
        return;
      }
      const page = Math.floor(index / option.pageSize);
      this.first.set(page * option.pageSize);
    });

    effect(() => {
      if (!this.filterable()) {
        return;
      }
      const filter = this.filterGlobal();
      this.table()?.filterGlobal(filter, 'contains');
    });
  }

  protected onTableFilter({ filteredValue }: TableFilterEvent) {
    this.displayCount.set(filteredValue?.length ?? this.data().length);
  }

  protected onTablePage(event: TablePageEvent): void {
    if (event.rows !== undefined) {
      this.pageSizeChange.emit(event.rows);
    }
  }

  protected onRowClick(row: T, event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (
      target.closest(
        'button, a, input, select, textarea, [role="button"], [data-row-action]',
      )
    ) {
      event.preventDefault();
      return;
    }
    const index = this.data().indexOf(row);
    this.rowClick.emit({ row, index, event });
  }

  protected onCellClick(
    row: T,
    column: TableColumnOption<T>,
    event: MouseEvent,
  ): void {
    const index = this.data().indexOf(row);
    this.cellClick.emit({ row, index, column, event });
  }

  protected onToggleRow(row: T, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const table = this.table();
    table.toggleRow(row);

    const index = this.data().indexOf(row);
    const expanded = table.isRowExpanded(row);
    this.rowToggle.emit({ row, index, expanded, event });
  }

  protected setColumnTemplate(
    option: TableColumnOption<T>,
  ): TemplateRef<unknown> | null {
    return isTemplateColumnOption(option) ? option.rowTemplate : null;
  }

  protected customSort({
    data,
    field,
    order,
  }: {
    data: T[];
    field: string;
    order: number;
  }): void {
    if (this.tableOptionWithDefaults().serverSort) {
      this.emitServerSort(field, order);
      return;
    }
    const columns = this.columns() ?? [];
    data.sort((a, b) => {
      const columnOption = columns.find(i => i.field == field);
      if (!columnOption) {
        return 0;
      }
      let _a, _b;
      if (columnOption.getFieldValue) {
        _a = columnOption.getFieldValue(a);
        _b = columnOption.getFieldValue(b);
      } else {
        const sortKey = field as keyof T;
        const lookup = (source: T, key: keyof T) =>
          typeof source[key] == 'string'
            ? (<string>source[key]).toLowerCase()
            : source[key];
        _a = lookup(a, sortKey);
        _b = lookup(b, sortKey);
      }
      let result = 0;
      if (_a < _b) {
        result = -1;
      } else if (_a > _b) {
        result = 1;
      }
      return order * result;
    });
  }

  private emitServerSort(field: string, order: number): void {
    const direction = order === 1 ? 'asc' : 'desc';
    if (
      this.lastServerSort?.field === field &&
      this.lastServerSort.direction === direction
    ) {
      return;
    }
    this.lastServerSort = { field, direction };
    this.sortChange.emit(this.lastServerSort);
  }

  protected canColumnSort(column: TableColumnOption<T>) {
    if (!this.tableOptionWithDefaults().sortable) {
      return false;
    }
    return isTextColumnOption(column) || column.getFieldValue;
  }

  protected isHeaderTemplate(
    header: string | TemplateRef<unknown>,
  ): header is TemplateRef<unknown> {
    return header instanceof TemplateRef;
  }
}
